import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api, formatApiErrorDetail } from '../lib/api';
import { Btn, Card, Input, Label, PageHeader, Select } from '../components/Ui';
import { StatusBadge, FlagBadges, DuplicateBadge } from '../components/Badges';
import { ArrowLeft, Save, Check, Download, Trash2, X, Loader2, AlertTriangle } from 'lucide-react';

// Status options as [value, label_es] — values stay technical, labels are user-friendly.
const STATUS_OPTIONS = [
  ['received', 'Recibido'],
  ['archived', 'Archivado'],
  ['check_required', 'Por revisar'],
  ['reviewed', 'Revisado'],
  ['sent_to_accountant', 'Enviado a asesoría'],
  ['no_pdf', 'Sin PDF'],
  ['failed', 'Fallido'],
  ['ignored', 'Ignorado'],
];

const REVIEWABLE_STATUSES = new Set(['check_required', 'failed', 'no_pdf', 'received']);

export default function DocumentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTyped, setDeleteTyped] = useState('');

  const load = async () => {
    const { data } = await api.get(`/api/documents/${id}`);
    setDoc(data);
    setForm({
      supplier: data.supplier || '',
      document_date: data.document_date || '',
      document_number: data.document_number || '',
      stored_filename: data.stored_filename || '',
      status: data.status,
      notes: data.notes || '',
    });
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const save = async (confirmReview = false) => {
    setError('');
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.document_date) delete body.document_date;
      // Only send stored_filename if changed (avoids spurious Wasabi rename)
      if (body.stored_filename === (doc?.stored_filename || '')) {
        delete body.stored_filename;
      }
      if (confirmReview) body.confirm_review = true;
      const { data } = await api.patch(`/api/documents/${id}`, body);
      setDoc((d) => ({ ...d, ...data, download_url: d?.download_url }));
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
      if (confirmReview) {
        toast.success('Documento marcado como revisado');
      }
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    const t = toast.loading('Eliminando documento…');
    try {
      const { data } = await api.delete(`/api/documents/${id}`);
      toast.dismiss(t);
      toast.success(`Documento #${id} eliminado`, {
        description: data.wasabi_key_deleted
          ? `Archivo Wasabi también borrado.`
          : data.wasabi_warning || 'Sin archivo Wasabi asociado.',
        duration: 6000,
      });
      if (data.wasabi_warning) {
        toast.warning('Archivo Wasabi no eliminado', {
          description: data.wasabi_warning,
          duration: 10000,
        });
      }
      setDeleteOpen(false);
      // Redirect back to documents list
      setTimeout(() => nav('/documents'), 300);
    } catch (e) {
      toast.dismiss(t);
      toast.error('Error al eliminar', {
        description: formatApiErrorDetail(e?.response?.data?.detail) || e.message,
        duration: 10000,
      });
      setDeleting(false);
    }
  };

  if (!doc) {
    return <div className="p-8 text-muted">Cargando…</div>;
  }

  const alreadyReviewed = doc.status === 'reviewed' || doc.status === 'sent_to_accountant';
  const canConfirm = REVIEWABLE_STATUSES.has(form.status);

  return (
    <>
      <PageHeader title={`Documento #${doc.id}`} subtitle={doc.company_name || ''} testid="doc-detail-header">
        <Btn variant="secondary" onClick={() => nav(-1)} data-testid="back-btn">
          <ArrowLeft size={14} /> Volver
        </Btn>
        <button
          data-testid="open-delete-btn"
          onClick={() => { setDeleteTyped(''); setDeleteOpen(true); }}
          title="Eliminar documento"
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide2 px-3 py-2 border-2 border-danger text-danger hover:bg-danger hover:text-white transition-colors"
        >
          <Trash2 size={14} /> Eliminar
        </button>
      </PageHeader>

      <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: PDF preview */}
        <Card testid="pdf-preview">
          <div className="border-b border-ink/90 px-4 py-3 flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-wide2">Vista previa PDF</h2>
            {doc.download_url && (
              <a
                data-testid="pdf-download"
                href={doc.download_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide2 text-accent hover:text-accentHover"
              >
                <Download size={14} /> Descargar
              </a>
            )}
          </div>
          {doc.download_url ? (
            <iframe
              data-testid="pdf-iframe"
              src={doc.download_url}
              title="PDF"
              className="w-full h-[70vh] border-0 bg-surface"
            />
          ) : (
            <div className="h-[70vh] flex items-center justify-center text-muted text-sm bg-surface">
              {doc.wasabi_key
                ? 'Wasabi no configurado en el backend — no se puede generar URL firmada'
                : 'Sin PDF adjunto'}
            </div>
          )}
          <div className="border-t border-border px-4 py-3 text-xs space-y-1 bg-surface">
            <div><span className="text-muted font-bold uppercase tracking-wide2 text-[10px]">Bucket</span> <span className="font-mono">{doc.wasabi_bucket || '—'}</span></div>
            <div><span className="text-muted font-bold uppercase tracking-wide2 text-[10px]">Clave</span> <span className="font-mono break-all">{doc.wasabi_key || '—'}</span></div>
            <div><span className="text-muted font-bold uppercase tracking-wide2 text-[10px]">Archivo original</span> <span className="font-mono">{doc.original_filename || '—'}</span></div>
          </div>
        </Card>

        {/* Right: metadata form */}
        <Card testid="metadata-form">
          <div className="border-b border-ink/90 px-4 py-3 flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-wide2">Metadatos</h2>
            <div className="flex items-center gap-2">
              <StatusBadge status={doc.status} />
              <DuplicateBadge count={doc.duplicate_count} />
              <FlagBadges flags={doc.flags} />
            </div>
          </div>

          <div className="p-5 space-y-4">
            {doc.duplicate_count > 0 && (
              <div data-testid="duplicate-block" className="border border-warn bg-warn/10 px-3 py-2 text-xs">
                🔁 Email recibido <strong>{doc.duplicate_count + 1} veces</strong>
                {doc.last_duplicate_at && (
                  <> (última el {new Date(doc.last_duplicate_at).toLocaleString()})</>
                )}
                {doc.last_duplicate_reason && (
                  <> — coincide con <code>{doc.last_duplicate_reason}</code></>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-muted text-[10px] font-bold uppercase tracking-wide2">Recibido</div>
                <div className="num">{doc.received_at ? new Date(doc.received_at).toLocaleString() : '—'}</div>
              </div>
              <div>
                <div className="text-muted text-[10px] font-bold uppercase tracking-wide2">Remitente</div>
                <div className="font-mono">{doc.sender_email || '—'}</div>
              </div>
              <div>
                <div className="text-muted text-[10px] font-bold uppercase tracking-wide2">Remitente original</div>
                <div className="font-mono">{doc.original_sender || '—'}</div>
              </div>
              <div>
                <div className="text-muted text-[10px] font-bold uppercase tracking-wide2">Destinatario</div>
                <div className="font-mono">{doc.recipient_email || '—'}</div>
              </div>
              <div className="col-span-2">
                <div className="text-muted text-[10px] font-bold uppercase tracking-wide2">Asunto</div>
                <div>{doc.subject || '—'}</div>
              </div>
            </div>

            <hr className="border-border" />

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Proveedor</Label>
                <Input
                  data-testid="meta-supplier"
                  value={form.supplier}
                  onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                />
              </div>
              <div>
                <Label>N° Documento</Label>
                <Input
                  data-testid="meta-number"
                  value={form.document_number}
                  onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                />
              </div>
              <div>
                <Label>Fecha documento</Label>
                <Input
                  data-testid="meta-date"
                  type="date"
                  value={form.document_date}
                  onChange={(e) => setForm({ ...form, document_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Estado</Label>
                <Select
                  data-testid="meta-status"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Nombre archivo (renombra también en Wasabi)</Label>
                <Input
                  data-testid="meta-filename"
                  value={form.stored_filename}
                  onChange={(e) => setForm({ ...form, stored_filename: e.target.value })}
                  placeholder="2026-06-04_proveedor_1138.pdf"
                  className="font-mono"
                />
                <p className="text-[10px] text-muted mt-1">
                  Sólo el nombre del archivo. La carpeta se conserva automáticamente.
                </p>
              </div>
              <div className="col-span-2">
                <Label>Notas</Label>
                <textarea
                  data-testid="meta-notes"
                  className="w-full h-20 px-3 py-2 bg-white border border-ink text-sm focus:outline-none focus:border-accent"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </div>
            </div>

            {error && (
              <div data-testid="meta-error" className="border border-danger text-danger text-xs px-3 py-2">
                {error}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Btn data-testid="save-btn" onClick={() => save(false)} disabled={saving} variant="secondary">
                <Save size={14} /> {saving ? 'Guardando…' : 'Guardar'}
              </Btn>
              <Btn
                data-testid="confirm-review-btn"
                onClick={() => save(true)}
                disabled={saving || !canConfirm}
                title={alreadyReviewed ? 'Ya revisado' : (!canConfirm ? 'No aplicable a este estado' : '')}
              >
                <Check size={14} /> Confirmar como revisado
              </Btn>
              {alreadyReviewed && (
                <span className="text-ok text-[10px] font-bold uppercase tracking-wide2 self-center">
                  ✓ Ya revisado
                </span>
              )}
              {savedFlash && (
                <span className="text-ok text-xs font-bold uppercase tracking-wide2 self-center" data-testid="saved-flash">
                  Guardado
                </span>
              )}
            </div>

            {doc.raw_text_preview && (
              <div className="mt-4 border border-border bg-surface p-3 text-xs">
                <div className="text-muted text-[10px] font-bold uppercase tracking-wide2 mb-1">
                  Vista previa texto extraído
                </div>
                <pre className="whitespace-pre-wrap font-mono text-[11px] leading-snug">
                  {doc.raw_text_preview}
                </pre>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Delete confirm modal */}
      {deleteOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => !deleting && setDeleteOpen(false)}
          data-testid="delete-modal-overlay"
        >
          <div
            className="bg-white border-2 border-ink max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="delete-modal"
          >
            <div className="border-b-2 border-ink px-5 py-3 bg-danger text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle size={18} />
                <h2 className="text-sm font-bold uppercase tracking-wide2">Eliminar documento</h2>
              </div>
              <button onClick={() => !deleting && setDeleteOpen(false)} className="hover:opacity-70" disabled={deleting}>
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-4 text-sm">
              <p>
                Vas a eliminar <strong>permanentemente</strong> el documento{' '}
                <code className="font-mono">#{doc.id}</code>:
              </p>
              <div className="border border-border bg-surface p-3 text-xs space-y-1">
                <div><strong>Proveedor:</strong> {doc.supplier || '—'}</div>
                <div><strong>Asunto:</strong> {doc.subject || '—'}</div>
                <div><strong>Recibido:</strong> {doc.received_at ? new Date(doc.received_at).toLocaleString() : '—'}</div>
                <div><strong>Estado:</strong> {doc.status}</div>
              </div>
              <p className="text-xs">
                {doc.wasabi_key ? (
                  <>El archivo en Wasabi{' '}
                    <code className="font-mono text-[10px] break-all">{doc.wasabi_key}</code>{' '}
                    <strong>también será borrado</strong>.</>
                ) : (
                  <>No hay archivo Wasabi asociado.</>
                )}
              </p>
              <div className="border-t border-border pt-3">
                <label className="block text-[10px] font-bold uppercase tracking-wide2 text-muted mb-1">
                  Escribe <code className="font-mono text-danger">ELIMINAR</code> para confirmar
                </label>
                <input
                  type="text"
                  data-testid="delete-confirm-input"
                  value={deleteTyped}
                  onChange={(e) => setDeleteTyped(e.target.value)}
                  placeholder="ELIMINAR"
                  disabled={deleting}
                  className="w-full px-3 py-2 bg-white border border-ink text-sm font-mono focus:outline-none focus:border-danger"
                />
              </div>
            </div>
            <div className="border-t-2 border-ink px-5 py-3 flex items-center justify-end gap-2 bg-surface">
              <button
                onClick={() => setDeleteOpen(false)}
                disabled={deleting}
                data-testid="delete-cancel-btn"
                autoFocus
                className="px-4 py-2 text-xs font-bold uppercase tracking-wide2 border-2 border-ink bg-white hover:bg-ink hover:text-white transition-colors"
              >
                No, cancelar
              </button>
              <button
                onClick={doDelete}
                disabled={deleting || deleteTyped !== 'ELIMINAR'}
                data-testid="delete-confirm-btn"
                className="px-4 py-2 text-xs font-bold uppercase tracking-wide2 border-2 border-danger bg-danger text-white hover:bg-white hover:text-danger transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader2 size={12} className="animate-spin" /> Eliminando…
                  </>
                ) : (
                  <>
                    <Trash2 size={12} /> Sí, eliminar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
